import React, { useState } from 'react'
import {
  Accordion, AccordionSummary, AccordionDetails, Grid, Typography, List, ListItem, Chip
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from '../detail-container.stylesheet'
import { MedicationEvaluationResult, ToxicityCriteria } from '../../../../model/evaluation.types'

interface IProps {
  classes: ClassNameMap
  evaluation: MedicationEvaluationResult
  defaultExpanded?: boolean
}

const MedicationEvaluationAccordionComponent: React.FC<IProps> = ({
  classes,
  evaluation,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded)

  const getStatusIcon = () => {
    switch (evaluation.evaluation_status) {
      case 'passed':
        return <CheckCircleIcon sx={{ color: 'var(--color-success)', fontSize: '1.2rem' }} />
      case 'failed':
        return <ErrorIcon sx={{ color: 'var(--color-error)', fontSize: '1.2rem' }} />
      case 'needs_attention':
        return <WarningIcon sx={{ color: 'var(--color-warning)', fontSize: '1.2rem' }} />
      case 'pending_toxicity_gate':
        return <HourglassEmptyIcon sx={{ color: 'var(--color-text-muted)', fontSize: '1.2rem' }} />
      default:
        return null
    }
  }

  const getStatusChipColor = (): 'success' | 'error' | 'warning' | 'default' => {
    switch (evaluation.evaluation_status) {
      case 'passed':
        return 'success'
      case 'failed':
        return 'error'
      case 'needs_attention':
        return 'warning'
      case 'pending_toxicity_gate':
        return 'default'
      default:
        return 'default'
    }
  }

  const getTitrationLabel = () => {
    const status = evaluation.titration_status
    if (status === 'initiate') return 'Initiate'
    if (status === 'uptitrate') return 'Uptitrate'
    if (status === 'downtitrate') return 'Downtitrate'
    if (status === 'discontinue') return 'Discontinue'
    if (status === 'maintain') return 'Maintain'
    return status?.replace(/_/g, ' ') || status
  }

  const getLabel = (val: string) => (
    <Typography className={classes.evalBoxLabel} variant="body2" color="textSecondary">
      {val}
    </Typography>
  )

  const getCriteriaList = (criteriaObj: ToxicityCriteria | undefined, isValue: boolean) => {
    if (!criteriaObj) return null
    return Object.keys(criteriaObj)?.map((key: string, index: number) => {
      const { value, dependents, dependent_has_override } = criteriaObj[key]
      const depenList = dependents && Object.keys(dependents)?.map((k: string) => `${k}: ${dependents[k]}`).join(', ')

      return value === isValue && (
        <List key={`criteria-${index}`} className={classes.criteriaList}>
          <ListItem
            className={classes.puList}
            sx={{
              width: '100%',
              backgroundColor: dependent_has_override ? '#ffff00cc !important' : 'inherit'
            }}
          >
            <Typography component="span" className={classes.evalBoxLabel} variant="body2" color="textSecondary">
              {key}
            </Typography>
            {depenList && (
              <Typography component="span" className={classes.evalBoxLabel} variant="body2" color="textSecondary">
                &nbsp;[{depenList}]
              </Typography>
            )}
          </ListItem>
        </List>
      )
    })
  }

  const renderCriteriaSection = (name: string, criteria: ToxicityCriteria | undefined) => {
    if (!criteria || Object.keys(criteria).length === 0) return null

    return (
      <Grid container direction="column" className={classes.evalSubBox}>
        <Grid item>{getLabel(name)}</Grid>
        <Grid item className={classes.fullWidth}>
          <Grid container direction="row" spacing={2} className={classes.evalSubBox}>
            <Grid item xs={6}>
              <Typography className={classes.evalBoxVal}><u>True</u></Typography>
              {getCriteriaList(criteria, true)}
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.evalBoxVal}><u>False</u></Typography>
              {getCriteriaList(criteria, false)}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const isPending = evaluation.evaluation_status === 'pending_toxicity_gate'

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 1,
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid var(--color-border-light)',
        borderRadius: '4px !important',
        backgroundColor: isPending ? 'var(--color-background-subtle)' : 'var(--color-paper)',
        opacity: isPending ? 0.7 : 1
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: '48px',
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 }
        }}
      >
        {getStatusIcon()}
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600 }}>
          {evaluation.medication_name}
        </Typography>
        <Chip
          label={getTitrationLabel()}
          size="small"
          sx={{ mr: 1, fontSize: '0.7rem' }}
        />
        <Chip
          label={evaluation.toxicity_evaluation || 'pending'}
          size="small"
          color={getStatusChipColor()}
          sx={{ fontSize: '0.7rem' }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 2 }}>
        <Grid className={classes.evalContent} container spacing={1}>
          {/* Recommendations */}
          <Grid item className={classes.fullWidth}>
            <Grid container direction="column">
              <Grid item>{getLabel('Recommendations')}</Grid>
              <Grid item className={classes.evalSubBox}>
                {evaluation.recommendation?.map((rec, idx) => (
                  <Typography key={`rec-${idx}`} className={classes.evalBoxVal}>
                    {rec}
                  </Typography>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Toxicity Evaluation */}
          <Grid item className={classes.fullWidth}>
            <Grid container direction="row" className={classes.evalToxCard}>
              <Grid item>{getLabel('Toxicity evaluation')}</Grid>
              <Grid item>
                <Typography className={classes.toxicityEvalBoxVal}>
                  {evaluation.toxicity_evaluation}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Toxicity Criteria */}
          <Grid item className={classes.fullWidth}>
            {renderCriteriaSection('Toxicity criteria', evaluation.toxicity_criteria)}
          </Grid>

          {/* Efficacy Evaluation */}
          {evaluation.efficacy_evaluation && (
            <Grid item className={classes.fullWidth}>
              <Grid container direction="row" className={classes.evalToxCard}>
                <Grid item>{getLabel('Efficacy evaluation')}</Grid>
                <Grid item>
                  <Typography className={classes.toxicityEvalBoxVal}>
                    {evaluation.efficacy_evaluation}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Efficacy Criteria */}
          <Grid item className={classes.fullWidth}>
            {renderCriteriaSection('Efficacy criteria', evaluation.efficacy_criteria)}
          </Grid>

          {/* Inefficacy Criteria */}
          {evaluation.inefficacy_criteria && (
            <Grid item className={classes.fullWidth}>
              {renderCriteriaSection('Inefficacy criteria', evaluation.inefficacy_criteria)}
            </Grid>
          )}

          {/* Next Stage */}
          {evaluation.next_stage && (
            <Grid item className={classes.fullWidth}>
              <Grid container direction="row" className={classes.evalToxCard}>
                <Grid item>{getLabel('Next stage')}</Grid>
                <Grid item>
                  <Typography className={classes.toxicityEvalBoxVal}>
                    {evaluation.next_stage?.replace('med', 'Medication')}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default withStyles(stylesheet)(MedicationEvaluationAccordionComponent)
